import React, { useEffect, useState } from "react";
import { X, Loader2, UploadCloud } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ImportHistoricalInvoiceDialog({ open, onClose, onImported }) {
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState("");
  const [number, setNumber] = useState("");
  const [issuedAt, setIssuedAt] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("unpaid");
  const [sentStatus, setSentStatus] = useState("not_sent");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecordId(""); setNumber(""); setIssuedAt(todayIso()); setAmount("");
    setDescription(""); setPaymentStatus("unpaid"); setSentStatus("not_sent"); setFile(null);
    api.get("/admin/client-records").then(r => setRecords(r.data.records || [])).catch(() => setRecords([]));
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!recordId) return toast.error("Sélectionnez une fiche client");
    if (!number.trim()) return toast.error("Renseignez le numéro de facture");
    if (!amount || Number(amount) <= 0) return toast.error("Montant invalide");
    if (!file) return toast.error("Merci de joindre le PDF de la facture");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("client_record_id", recordId);
      fd.append("number", number.trim());
      fd.append("issued_at", issuedAt);
      fd.append("amount", String(Number(amount)));
      fd.append("description", description.trim());
      fd.append("payment_status", paymentStatus);
      fd.append("sent_status", sentStatus);
      fd.append("pdf", file);
      const { data } = await api.post("/admin/invoices/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Facture ${data.number} importée`);
      onImported?.(data);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recordLabel = (r) => r.company_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.billing_email || "Fiche sans nom";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="import-invoice-modal">
      <div className="cs-card p-6 max-w-2xl w-full my-8 relative bg-[#FAF8F5]">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-500 hover:text-black"><X size={18} /></button>
        <div className="cs-eyebrow text-[#0E7C90]">Facture existante</div>
        <h3 className="cs-h3 mt-1 text-2xl">Importer une facture historique</h3>
        <p className="mt-1 text-[13px] text-neutral-500">Le PDF importé est conservé tel quel. Le numéro renseigné n'entre pas dans la numérotation automatique du site.</p>

        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="cs-eyebrow text-neutral-500">Fiche client *</span>
            <select className="cs-input mt-1 w-full" value={recordId} onChange={e => setRecordId(e.target.value)} data-testid="import-record">
              <option value="">— Choisir une fiche —</option>
              {records.map(r => <option key={r.id} value={r.id}>{recordLabel(r)} · {r.billing_email}</option>)}
            </select>
            {records.length === 0 && <span className="mt-1 text-[12px] text-amber-700">Aucune fiche disponible — créez-en une dans l'onglet « Fiches clients ».</span>}
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Numéro de facture *</span>
              <input className="cs-input mt-1 w-full" value={number} onChange={e => setNumber(e.target.value)} placeholder="Ex. 2024-036" data-testid="import-number" />
            </label>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Date de facture *</span>
              <input type="date" className="cs-input mt-1 w-full" value={issuedAt} onChange={e => setIssuedAt(e.target.value)} data-testid="import-date" />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Montant TTC (€) *</span>
              <input type="number" step="0.01" className="cs-input mt-1 w-full" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Ex. 480" data-testid="import-amount" />
            </label>
            <label className="block">
              <span className="cs-eyebrow text-neutral-500">Intitulé / objet</span>
              <input className="cs-input mt-1 w-full" value={description} onChange={e => setDescription(e.target.value)} placeholder="Ex. Mise en page rapport annuel" data-testid="import-description" />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className="cs-eyebrow text-neutral-500">Statut de paiement</span>
              <div className="mt-1 flex gap-2">
                {[{ v: "unpaid", l: "À régler" }, { v: "paid", l: "Payée" }].map(o => (
                  <button key={o.v} type="button" onClick={() => setPaymentStatus(o.v)}
                    className={`flex-1 px-3 py-2 border text-sm ${paymentStatus === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
                    data-testid={`import-pay-${o.v}`}>{o.l}</button>
                ))}
              </div>
            </div>
            <div>
              <span className="cs-eyebrow text-neutral-500">Statut d'envoi</span>
              <div className="mt-1 flex gap-2">
                {[{ v: "not_sent", l: "Non envoyée" }, { v: "sent", l: "Envoyée" }].map(o => (
                  <button key={o.v} type="button" onClick={() => setSentStatus(o.v)}
                    className={`flex-1 px-3 py-2 border text-sm ${sentStatus === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
                    data-testid={`import-sent-${o.v}`}>{o.l}</button>
                ))}
              </div>
            </div>
          </div>

          <label className="block border-2 border-dashed border-black/25 hover:border-black/50 p-4 cursor-pointer bg-white transition-colors">
            <span className="cs-eyebrow text-neutral-500 inline-flex items-center gap-2"><UploadCloud size={14} /> PDF de la facture *</span>
            <input type="file" accept="application/pdf,.pdf" className="mt-2 block text-sm" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="import-pdf" />
            {file && <div className="mt-1 text-[12px] font-mono text-neutral-700">{file.name} · {(file.size / 1024).toFixed(0)} Ko</div>}
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="cs-btn-ghost !text-black !border-black">Annuler</button>
          <button onClick={submit} disabled={busy} className="cs-btn-primary disabled:opacity-40 inline-flex items-center gap-2" data-testid="import-submit">
            {busy && <Loader2 size={14} className="animate-spin" />} Enregistrer la facture
          </button>
        </div>
      </div>
    </div>
  );
}
